import React from 'react';
import { TeButtonMinimal, TeText } from '@topcon/tuicl-react';

interface ErrorNotificationCardProps {
  readonly title: string;
  readonly description: string;
  readonly moreInfoLabel: string;
  readonly onMoreInfo: () => void;
  readonly onClose: () => void;
}

const CARD_WIDTH = 360;
const DESCRIPTION_MAX_LINES = 2;

const cardStyle: React.CSSProperties = {
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  width: CARD_WIDTH,
  padding: 16,
  backgroundColor: 'var(--te-color-surface, #ffffff)',
  borderLeft: '4px solid var(--te-color-status-error, #d0021b)',
  borderRadius: 4,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 8,
};

const descriptionStyle: React.CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: DESCRIPTION_MAX_LINES,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

/**
 * Compact error notification shown before the full drawer. Displays the error
 * title, a short (two-line) description, a link that expands the full drawer,
 * and a dismiss button. Presentational only; the caller owns the state.
 * @param props - Title, description, link label, and the expand/close callbacks.
 * @returns The compact error notification card.
 */
function ErrorNotificationCard({
  title,
  description,
  moreInfoLabel,
  onMoreInfo,
  onClose,
}: ErrorNotificationCardProps): React.JSX.Element {
  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <TeText size={16} weight={700}>
          {title}
        </TeText>
        <TeButtonMinimal icon="close" variant="neutral" size="s" teclick={onClose} />
      </div>

      <TeText size={14} weight={400} color="text.secondary" style={descriptionStyle}>
        {description}
      </TeText>

      <TeButtonMinimal
        text={moreInfoLabel}
        variant="primary"
        size="s"
        postfixIcon="arrow_forward"
        teclick={onMoreInfo}
      />
    </div>
  );
}

export default ErrorNotificationCard;
